// Copyright ©️ 2026 eVolve MEP, LLC

import {
  type CompanyMember,
  type User,
  type AssignableRole,
  type Entitlement,
  extractErrorMessage,
  useEvolveDelete,
  useEvolvePatch,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { hasLengthExactly, type StrongOmit } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';
import type { RoleToRemove } from 'modules/Admin/CompanyMembers/types';

type CommonActionModalProps = {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

type SingleUserActionModalProps = CommonActionModalProps & {
  singleUserAction: CompanyMember | null;
};

type MultiUserActionModalProps = CommonActionModalProps & {
  usersToAction: CompanyMember[];
};

export const DeleteMembersModal = ({
  opened,
  onClose,
  user,
  onSuccess,
  usersToAction,
}: MultiUserActionModalProps & {
  user: User;
}) => {
  const { apiCall: deleteUsersApiCall, loading: deleting } = useEvolvePatch('admin/user/multiDelete');

  return (
    <WrappedModal
      title={`Delete member${usersToAction.length === 1 ? '' : 's'}`}
      opened={opened}
      onClose={onClose}
      loading={deleting}
      confirmationText="Delete"
      buttonColor="red"
      onConfirm={async () => {
        const userIds = usersToAction.filter((u) => u.userId !== user.userId).map((r) => r.userId);
        void deleteUsersApiCall({ userIds }).then(() => {
          notifications.show({
            title: 'Successfully deleted',
            message: `Deleted ${userIds.length} member${userIds.length === 1 ? '' : 's'}`,
            color: 'green',
          });
          onSuccess();
        });
      }}
    >
      <Text>
        Are you sure you want to delete{' '}
        <b>{hasLengthExactly(usersToAction, 1) ? usersToAction[0].email : `${usersToAction.length} members`}</b>?
      </Text>
    </WrappedModal>
  );
};

export const RemoveRoleListFromUserModal = ({
  removeRole,
  endRemoveRole,
  onRoleRemoved,
  assignableRoles,
}: {
  removeRole: RoleToRemove | null;
  endRemoveRole: () => void;
  onRoleRemoved: () => void;
  assignableRoles: AssignableRole[] | undefined;
}) => {
  const { apiCall: doRemoveRole, loading: removingRole } = useEvolvePost('admin/role/remove');

  return (
    <WrappedModal
      title="Confirm"
      opened={isNotNil(removeRole)}
      onClose={endRemoveRole}
      loading={removingRole}
      onConfirm={async () => {
        if (isNotNil(removeRole)) {
          await doRemoveRole({
            userRoleAssignments: [{ userId: removeRole.userId, roleId: removeRole.roleId }],
          }).then(() => {
            onRoleRemoved();
            notifications.show({
              title: 'Success',
              message: 'Removed role successfully',
              color: 'green',
            });
          });
        }
      }}
    >
      Do you want to remove role <b>{assignableRoles?.find((r) => r.roleId === removeRole?.roleId)?.displayName}</b>{' '}
      from <b>{removeRole?.userDisplayName}</b>?
    </WrappedModal>
  );
};

export const RemoveAllRolesFromUserModal = ({
  opened,
  onClose,
  singleUserAction,
  onSuccess,
}: SingleUserActionModalProps) => {
  const { apiCall: doRemoveRole, loading: removingRole } = useEvolvePost('admin/role/remove');

  return (
    <WrappedModal
      title="Remove roles"
      opened={opened}
      loading={removingRole}
      onClose={onClose}
      confirmationText="Remove"
      buttonColor="red"
      onConfirm={async () => {
        if (isNotNil(singleUserAction)) {
          await doRemoveRole({
            userRoleAssignments: [
              singleUserAction.mechanicalRoleId,
              singleUserAction.electricalRoleId,
              singleUserAction.shopRoleId,
            ]
              .filter(isNotNil)
              .map((roleId) => ({
                userId: singleUserAction.userId,
                roleId,
              })),
          }).then(() => {
            notifications.show({
              title: 'Successfully removed roles',
              message: `All roles successfully removed from ${singleUserAction.email}`,
            });
            onSuccess();
          });
        }
      }}
    >
      <Text>
        Remove all roles for <b>{singleUserAction?.email}</b>?
      </Text>
    </WrappedModal>
  );
};

export const AddAdminModal = ({ opened, onClose, usersToAction, onSuccess }: MultiUserActionModalProps) => {
  const { apiCall: addAdmin, loading: addingAdmin } = useEvolvePost('admin/role/assignCompanyAdmin');
  return (
    <WrappedModal
      title="Assign admin"
      opened={opened}
      onClose={onClose}
      loading={addingAdmin}
      confirmationText="Assign"
      onConfirm={async () => {
        await addAdmin(usersToAction.filter((u) => !u.isAdmin).map((u) => u.userId)).then(() => {
          notifications.show({
            title: 'Success',
            message: `${usersToAction.length} member${usersToAction.length === 1 ? '' : 's'} successfully added as admin${usersToAction.length === 1 ? '' : 's'}.`,
            color: 'green',
          });
          onSuccess();
        });
      }}
    >
      <Text>
        Do you want to make{' '}
        {hasLengthExactly(usersToAction, 1) ? (
          <>
            <b>{usersToAction[0].email}</b> an admin
          </>
        ) : (
          <>
            <b>{usersToAction.length} members</b> admins
          </>
        )}
        ?
      </Text>
    </WrappedModal>
  );
};

export const RemoveAdminModal = ({ opened, onClose, usersToAction, onSuccess }: MultiUserActionModalProps) => {
  const { apiCall: removeAdmin, loading: removingAdmin } = useEvolvePost('admin/role/removeCompanyAdmin');
  return (
    <WrappedModal
      title="Remove admin"
      opened={opened}
      onClose={onClose}
      loading={removingAdmin}
      confirmationText="Remove"
      buttonColor="red"
      onConfirm={async () => {
        await removeAdmin(usersToAction.map((u) => u.userId)).then(() => {
          notifications.show({
            title: 'Success',
            message: `Removed admin privileges from ${usersToAction.length} members${usersToAction.length === 1 ? '' : 's'}.`,
            color: 'green',
          });
          onSuccess();
        });
      }}
    >
      <Text>
        Do you want to remove admin privileges from{' '}
        <b>{hasLengthExactly(usersToAction, 1) ? usersToAction[0].email : <>{usersToAction.length} members</>}</b>?
      </Text>
    </WrappedModal>
  );
};

export const PasswordResetModal = ({
  opened,
  onClose,
  usersToAction,
}: StrongOmit<MultiUserActionModalProps, 'onSuccess'>) => {
  const { apiCall: sendPasswordResetEmail, loading } = useEvolvePost('admin/authentication/forgotPassword', {
    alertOnError: false,
  });
  const handleReset = async () => {
    await Promise.allSettled(
      usersToAction
        .filter((u) => u.status === 'Active')
        .map(({ email }) =>
          sendPasswordResetEmail({ email }).catch((error) => {
            notifications.show({
              title: `Error (${email})`,
              // AWS errors tend to have the useful error on error.message
              // oxlint-disable-next-line typescript/no-unsafe-member-access
              message: error?.message ?? extractErrorMessage(error),
              color: 'red',
            });
            throw error;
          }),
        ),
    ).then((results) => {
      const sent = results.filter((r) => r.status === 'fulfilled').length;
      if (sent > 0) {
        notifications.show({
          title: 'Successfully sent',
          message: `${sent} password reset email${sent === 1 ? '' : 's'} sent`,
          color: 'green',
        });
      }
    });
  };
  return (
    <WrappedModal
      title="Reset password"
      confirmationText="Reset"
      opened={opened}
      onClose={onClose}
      loading={loading}
      onConfirm={handleReset}
    >
      <Text>
        Do you want to reset the password for{' '}
        <b>
          {usersToAction.length} user{usersToAction.length === 1 ? '' : 's'}
        </b>
        ?
      </Text>
    </WrappedModal>
  );
};

export const RemoveLicenseModal = ({
  entitlement,
  onClose,
  onSuccess,
}: {
  entitlement: Entitlement | null;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const { apiCall: unassignEntitlement, loading } = useEvolveDelete(`admin/entitlement/:entitlementId`);

  return (
    <VariableTrackingModal
      title="Remove license"
      confirmationText="Remove"
      buttonColor="red"
      variable={entitlement}
      onClose={onClose}
      loading={loading}
      onConfirm={async ({ entitlementId }) => {
        await unassignEntitlement({ url: `admin/entitlement/${entitlementId}` }).then(() => {
          notifications.show({
            title: 'Successfully removed',
            message: `${entitlement?.productPool.product.productNameFormatted} license removed`,
            color: 'green',
          });
          onSuccess();
        });
      }}
    >
      <Text>
        Are you sure you want to remove the <b>{entitlement?.productPool.product.productNameFormatted}</b> license from
        this user?
      </Text>
    </VariableTrackingModal>
  );
};
